import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { errorMessage, useTRPC, useTRPCClient } from "../../../lib/api";
import { usePresence } from "../../../lib/use-presence";
import { Button, ErrorText, PageHeader, Section, StatusText, TextField } from "../../../ui";
import { useToast } from "../../../ui/toast";
import { SettingsNav } from "../SettingsNav";

const when = (d: Date | null) => (d === null ? "—" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }));

/**
 * Settings → Security: the person's passkeys. A passkey is what lets this browser approve sends
 * and destructive actions; the command line and agents share the Firebase sign-in but cannot use
 * Touch ID, so they cannot approve their own requests (architecture §9).
 */
export function SecurityPage() {
  const trpc = useTRPC();
  const status = useQuery(trpc.presence.status.queryOptions());
  const hasPasskey = status.data?.hasPasskey === true;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Security" />
      <SettingsNav />
      <Section
        title="Passkeys"
        description="Approving emails and destructive changes needs Touch ID or a security key. That way an agent using your command-line sign-in can't approve its own requests."
      >
        {status.isPending ? <StatusText>Checking…</StatusText> : null}
        {status.isError ? <ErrorText>{errorMessage(status.error)}</ErrorText> : null}
        {status.data === undefined ? null : hasPasskey ? (
          <StatusText>You have a passkey. Approvals in this browser ask for it.</StatusText>
        ) : (
          <StatusText>You have no passkey yet. Add one to approve requests.</StatusText>
        )}
        {hasPasskey ? <PasskeyTable /> : null}
        <AddPasskeyForm />
      </Section>
      {hasPasskey ? <UnlockSection /> : null}
    </div>
  );
}

function useRefreshPasskeys() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.presence.status.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.presence.list.queryKey() }),
    ]);
}

function PasskeyTable() {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const toast = useToast();
  const refresh = useRefreshPasskeys();
  const { ensurePresence } = usePresence();
  const list = useQuery(trpc.presence.list.queryOptions());
  const [busy, setBusy] = useState<string | null>(null);

  async function remove(id: string, label: string) {
    setBusy(id);
    try {
      // Removing a passkey is guarded like an approval: confirm with a passkey first.
      if ((await ensurePresence()) !== "present") return;
      await client.presence.remove.mutate({ credentialId: id });
      toast({ message: `Removed ${label}.` });
      await refresh();
    } catch (error) {
      toast({ tone: "error", message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  if (list.isError) return <ErrorText>{errorMessage(list.error)}</ErrorText>;
  if (list.data === undefined) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">Your passkeys</caption>
        <thead className="text-xs uppercase tracking-wide text-muted">
          <tr>
            <th scope="col" className="py-2 pr-4 font-medium">
              Name
            </th>
            <th scope="col" className="hidden py-2 pr-4 font-medium sm:table-cell">
              Added
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Last used
            </th>
            <th scope="col" className="py-2 font-medium">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.data.map((p) => (
            <tr key={p.id} className="border-t border-border">
              <td className="break-words py-2 pr-4 font-medium text-text">{p.label}</td>
              <td className="hidden py-2 pr-4 text-muted sm:table-cell">{when(p.createdAt)}</td>
              <td className="py-2 pr-4 text-muted">{when(p.lastUsedAt)}</td>
              <td className="py-2 text-right">
                <Button variant="danger" disabled={busy !== null} onClick={() => void remove(p.id, p.label)} aria-label={`Remove ${p.label}`}>
                  Remove
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddPasskeyForm() {
  const toast = useToast();
  const refresh = useRefreshPasskeys();
  const { registerPasskey } = usePresence();
  const [label, setLabel] = useState("");
  const add = useMutation({
    mutationFn: (name: string) => registerPasskey(name),
    onSuccess: async (outcome, name) => {
      if (outcome === "cancelled") {
        toast({ message: "No passkey added: the prompt was cancelled." });
        return;
      }
      setLabel("");
      toast({ message: `Added ${name}.` });
      await refresh();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    add.mutate(label.trim());
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
      <TextField
        label="Passkey name"
        hint="So you can tell them apart, e.g. MacBook Touch ID or YubiKey."
        required
        maxLength={60}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="flex-1"
      />
      <Button type="submit" variant="primary" disabled={add.isPending || label.trim().length === 0}>
        {add.isPending ? "Waiting for your passkey…" : "Add a passkey"}
      </Button>
      {add.isError ? <ErrorText>{errorMessage(add.error)}</ErrorText> : null}
    </form>
  );
}

/** "Unlock for 12 h": one Touch ID, then approvals in this tab go straight through until then. */
function UnlockSection() {
  const toast = useToast();
  const { present, expiresAt, ensurePresence, lock } = usePresence();
  const [busy, setBusy] = useState(false);

  async function unlock() {
    setBusy(true);
    try {
      if ((await ensurePresence()) === "cancelled") toast({ message: "Still locked: the passkey check was cancelled." });
    } catch (error) {
      toast({ tone: "error", message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="This browser" description="The unlock is kept in this tab's memory only; closing or reloading the tab locks it again.">
      {present && expiresAt !== null ? (
        <div className="flex flex-wrap items-center gap-3">
          <StatusText>Unlocked until {expiresAt.toLocaleTimeString(undefined, { timeStyle: "short" })}.</StatusText>
          <Button onClick={lock}>Lock now</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <StatusText>Locked. Approvals ask for your passkey.</StatusText>
          <Button disabled={busy} onClick={() => void unlock()}>
            Unlock for 12 h
          </Button>
        </div>
      )}
    </Section>
  );
}
